import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CALL_GROUPS,
  type AppelsStats,
  type Call,
  type CallRequest,
} from "@shared/modules/x-appels";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Input, Select } from "../../../core/components/ui/input";
import { PageHeader } from "../../../core/components/ui/page-header";
import { useAuthStore } from "../../../core/stores/auth";
import { useToastStore } from "../../../core/stores/toast";
import { CallRow } from "../components/call-row";
import { LiveCall } from "../components/live-call";
import { RequestCard } from "../components/request-card";
import {
  getStats,
  listCalls,
  listRequests,
  listScenarios,
  resendRequestSms,
  simulateCall,
  updateRequest,
} from "../lib/api";
import { useLiveCalls } from "../lib/live";

/**
 * Appels : ce qui se passe maintenant (appels en direct, transcription qui
 * défile), les chiffres du jour, puis l'historique par onglets avec recherche.
 */
export function AppelsPage() {
  const navigate = useNavigate();
  const push = useToastStore((s) => s.push);
  const isAdmin = useAuthStore((s) => s.user?.role === "admin");
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = CALL_GROUPS.find((g) => g.key === searchParams.get("onglet")) ?? CALL_GROUPS[0]!;
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [items, setItems] = useState<Call[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [stats, setStats] = useState<AppelsStats | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [scenarios, setScenarios] = useState<Array<{ key: string; label: string }>>([]);
  const [scenario, setScenario] = useState("");
  const [simulating, setSimulating] = useState(false);
  // Demandes de Léo (rappel, annulation) encore à traiter : en tête, avant l'historique.
  const [requests, setRequests] = useState<CallRequest[]>([]);
  const [requestBusy, setRequestBusy] = useState<string | null>(null);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  const { live, failed: liveFailed } = useLiveCalls(reload);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listCalls({
      status: tab.statuses.length ? tab.statuses : undefined,
      outcome: tab.outcomes.length ? tab.outcomes : undefined,
      q: debounced || undefined,
      limit: 300,
    })
      .then((list) => {
        if (cancelled) return;
        // Les appels en cours vivent dans le bloc « En direct », pas dans l'historique.
        setItems(list.filter((c) => c.status !== "en_cours"));
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    getStats()
      .then((s) => {
        if (!cancelled) setStats(s);
      })
      .catch(() => {});
    listRequests({ status: "a_traiter", limit: 50 })
      .then((list) => {
        if (!cancelled) setRequests(list);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tab, debounced, reloadKey]);

  const handleRequest = async (r: CallRequest) => {
    setRequestBusy(r.id);
    try {
      await updateRequest(r.id, { status: "traitee" });
      setRequests((list) => list.filter((x) => x.id !== r.id));
      push("success", "Demande traitée");
    } catch {
      push("danger", "Mise à jour impossible");
    } finally {
      setRequestBusy(null);
    }
  };
  const resendRequest = async (r: CallRequest) => {
    setRequestBusy(r.id);
    try {
      const next = await resendRequestSms(r.id);
      setRequests((list) => list.map((x) => (x.id === next.id ? next : x)));
      push(
        next.smsStatus === "echec" ? "danger" : "success",
        next.smsStatus === "echec" ? "Texto toujours en échec" : "Texto renvoyé",
      );
    } catch {
      push("danger", "Renvoi impossible");
    } finally {
      setRequestBusy(null);
    }
  };
  const smsFailed = requests.filter((r) => r.smsStatus === "echec").length;

  useEffect(() => {
    if (!isAdmin) return;
    listScenarios()
      .then(setScenarios)
      .catch(() => setScenarios([]));
  }, [isAdmin]);

  const selectTab = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("onglet", key);
    setSearchParams(next, { replace: true });
  };

  const simulate = async () => {
    if (simulating) return;
    setSimulating(true);
    try {
      await simulateCall(scenario || undefined);
      push("info", "Appel simulé lancé", "La transcription arrive en direct ci-dessous.");
    } catch {
      push("danger", "Simulation impossible");
    } finally {
      setSimulating(false);
    }
  };

  const todo = useMemo(
    () =>
      items.filter(
        (c) =>
          !c.handled &&
          (c.outcome === "message" || c.outcome === "rappel" || c.status === "manque"),
      ).length,
    [items],
  );

  return (
    <div className="page appels">
      <PageHeader
        title="Appels"
        subtitle="Ce que le répondeur IA a pris au téléphone : en direct, puis l'historique."
        actions={
          isAdmin && stats?.simulationAvailable ? (
            <span className="appels__simulate">
              <Select
                aria-label="Scénario"
                value={scenario}
                onChange={(e) => setScenario(e.target.value)}
              >
                <option value="">Scénario au hasard</option>
                {scenarios.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </Select>
              <Button
                variant="primary"
                icon={<Icon name="play" size={16} />}
                disabled={simulating}
                onClick={() => void simulate()}
              >
                Simuler un appel
              </Button>
            </span>
          ) : isAdmin ? (
            <Button
              variant="ghost"
              icon={<Icon name="sparkles" size={16} />}
              onClick={() => navigate("/appels/repondeur")}
            >
              Répondeur IA
            </Button>
          ) : undefined
        }
      />

      <div className="appels-kpis" aria-label="Aujourd'hui">
        <div className="appels-kpi">
          <span className="appels-kpi__label">Appels aujourd'hui</span>
          <span className="appels-kpi__value">{stats ? stats.today : "—"}</span>
        </div>
        <div className={`appels-kpi${live.length > 0 ? " appels-kpi--live" : ""}`}>
          <span className="appels-kpi__label">En cours</span>
          <span className="appels-kpi__value">{live.length}</span>
        </div>
        <div className="appels-kpi appels-kpi--good">
          <span className="appels-kpi__label">RDV pris</span>
          <span className="appels-kpi__value">{stats ? stats.bookedToday : "—"}</span>
        </div>
        <div className={`appels-kpi${stats && stats.missedToday > 0 ? " appels-kpi--alert" : ""}`}>
          <span className="appels-kpi__label">Manqués</span>
          <span className="appels-kpi__value">{stats ? stats.missedToday : "—"}</span>
        </div>
        <div className={`appels-kpi${todo > 0 ? " appels-kpi--alert" : ""}`}>
          <span className="appels-kpi__label">À traiter</span>
          <span className="appels-kpi__value">{todo}</span>
        </div>
        <div className={`appels-kpi${requests.length > 0 ? " appels-kpi--alert" : ""}`}>
          <span className="appels-kpi__label">Demandes de Léo</span>
          <span className="appels-kpi__value">{requests.length}</span>
        </div>
      </div>

      {requests.length > 0 ? (
        <section className="appels-requests" aria-label="Demandes à traiter">
          <h2 className="appels-live__title">
            Demandes de Léo à traiter
            <span className="appels-live__count">{requests.length}</span>
          </h2>
          {smsFailed > 0 ? (
            <p className="appels-requests__alert" role="alert">
              <Icon name="alert-circle" size={18} /> {smsFailed} texto{smsFailed > 1 ? "s" : ""} au
              propriétaire non envoyé{smsFailed > 1 ? "s" : ""} : vérifiez le cellulaire réglé au
              Répondeur IA et les clés Twilio, puis renvoyez.
            </p>
          ) : null}
          <div className="appels-requests__grid">
            {requests.map((r) => (
              <RequestCard
                key={r.id}
                request={r}
                busy={requestBusy === r.id}
                onHandled={(x) => void handleRequest(x)}
                onResend={(x) => void resendRequest(x)}
              />
            ))}
          </div>
        </section>
      ) : null}

      <section className="appels-live" aria-label="En direct">
        <h2 className="appels-live__title">
          <span
            className={`appels-live__dot${live.length > 0 ? " is-on" : ""}`}
            aria-hidden="true"
          />
          En direct
          {live.length > 0 ? <span className="appels-live__count">{live.length}</span> : null}
        </h2>
        {liveFailed ? (
          <p className="appels-live__empty">
            Suivi en direct indisponible — réessayez dans un instant.
          </p>
        ) : live.length === 0 ? (
          <p className="appels-live__empty">
            Aucun appel en cours. L'assistante décroche dès que ça sonne.
          </p>
        ) : (
          <div className="appels-live__grid">
            {live.map((c) => (
              <LiveCall key={c.id} call={c} />
            ))}
          </div>
        )}
      </section>

      <div className="chips chips--tabs" role="tablist" aria-label="Filtre">
        {CALL_GROUPS.map((g) => (
          <button
            key={g.key}
            type="button"
            role="tab"
            aria-selected={tab.key === g.key}
            className={`chips__chip${tab.key === g.key ? " is-active" : ""}`}
            onClick={() => selectTab(g.key)}
          >
            {g.label}
          </button>
        ))}
      </div>

      <div className="atelier-toolbar">
        <div className="atelier-toolbar__search">
          <Input
            type="search"
            aria-label="Rechercher un appel"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Nom, numéro, véhicule, sujet…"
            enterKeyHint="search"
          />
        </div>
        <div className="atelier-toolbar__spacer" />
        <span className="atelier-toolbar__count">
          {loading ? "…" : `${items.length} appel${items.length > 1 ? "s" : ""}`}
        </span>
      </div>

      {!loading && items.length === 0 ? (
        <p className="empty">
          {failed
            ? "Historique indisponible — réessayez dans un instant."
            : debounced
              ? "Aucun appel ne correspond."
              : "Aucun appel dans cet onglet."}
        </p>
      ) : loading && items.length === 0 ? (
        <p className="empty">Chargement…</p>
      ) : (
        <div className="call-list" role="list">
          {items.map((c) => (
            <CallRow key={c.id} call={c} />
          ))}
        </div>
      )}
    </div>
  );
}
